import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Check, Plus, X, User, Users, Building2 } from "lucide-react"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import type { Project, ProjectTag, SharedProps, TagScope } from "@/types"

const PRESET_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#ef4444", "#f97316",
  "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6",
]

const SCOPE_OPTIONS: { value: number; label: string; scope: TagScope; icon: typeof User }[] = [
  { value: 2, label: "Privé", scope: "user", icon: User },
  { value: 0, label: "Équipes", scope: "team", icon: Users },
  { value: 1, label: "Organisation", scope: "organisation", icon: Building2 },
]

type Props = {
  project: Project
  availableTags: ProjectTag[]
  onClose?: () => void
}

export function TagPopoverContent({ project, availableTags, onClose }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const [newName, setNewName] = useState("")
  const [newColor, setNewColor] = useState(PRESET_COLORS[0])
  const [newScope, setNewScope] = useState(2)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const currentTagIds = new Set(project.project_tags?.map((t) => t.id) || [])

  const toggleTag = (tagId: string) => {
    const newIds = currentTagIds.has(tagId)
      ? [...currentTagIds].filter((id) => id !== tagId)
      : [...currentTagIds, tagId]
    router.patch(`/projects/${project.id}/update_tags`, { project_tag_ids: newIds }, { preserveScroll: true })
    onClose?.()
  }

  const handleCreate = () => {
    if (!newName.trim()) return
    router.post(
      "/project_tags",
      { name: newName.trim(), color: newColor, scope: newScope },
      { preserveScroll: true, onSuccess: () => { setNewName(""); setShowCreate(false) } },
    )
  }

  const handleDelete = () => {
    if (!confirmDeleteId) return
    router.delete(`/project_tags/${confirmDeleteId}`, { preserveScroll: true })
    setConfirmDeleteId(null)
  }

  const canDelete = (tag: ProjectTag) => {
    if (!current_user) return false
    if (current_user.is_super_admin) return true
    if (current_user.role === "admin") return true
    return tag.user_id === current_user.id
  }

  return (
    <>
      {/* Tag list */}
      <div className="p-1.5">
        {availableTags.length > 0 ? (
          <div className="flex flex-col gap-px">
            {availableTags.map((tag) => {
              const isSelected = currentTagIds.has(tag.id)
              return (
                <div
                  key={tag.id}
                  className="group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-accent"
                >
                  <button
                    onClick={() => toggleTag(tag.id)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
                  >
                    <span
                      className="size-3 shrink-0 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="truncate text-sm">{tag.name}</span>
                    <Check
                      className={cn(
                        "ml-auto size-4 shrink-0 transition-opacity",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </button>
                  {canDelete(tag) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(tag.id) }}
                      className="text-foreground shrink-0 cursor-pointer opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-muted-foreground px-2 py-3 text-center text-sm">
            Aucun tag disponible
          </p>
        )}
      </div>

      <Separator />

      {/* Create section */}
      {showCreate ? (
        <div className="flex flex-col gap-3 p-3">
          <Input
            placeholder="Nom du tag..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            className="h-8"
            autoFocus
          />

          {/* Color picker */}
          <div className="flex items-center gap-1.5">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={cn(
                  "flex size-6 cursor-pointer items-center justify-center rounded-full transition-transform",
                  newColor === c && "ring-ring ring-2 ring-offset-2 ring-offset-popover",
                )}
                style={{ backgroundColor: c }}
              >
                {newColor === c && <Check className="size-3 text-white" />}
              </button>
            ))}
          </div>

          {/* Scope selector */}
          <div data-slot="button-group" className="isolate flex">
            {SCOPE_OPTIONS.map((opt) => {
              const Icon = opt.icon
              return (
                <Button
                  key={opt.value}
                  type="button"
                  variant={newScope === opt.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNewScope(opt.value)}
                  className="flex-1 first:rounded-r-none last:rounded-l-none [&:not(:first-child):not(:last-child)]:rounded-none [&:not(:first-child)]:-ml-px"
                >
                  <Icon className="size-3.5" />
                  {opt.label}
                </Button>
              )
            })}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={!newName.trim()} size="sm" className="flex-1">
              Créer
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
              Annuler
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-1.5">
          <button
            onClick={() => setShowCreate(true)}
            className="text-muted-foreground hover:bg-accent hover:text-foreground flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors"
          >
            <Plus className="size-4" />
            Nouveau tag
          </button>
        </div>
      )}

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le tag</AlertDialogTitle>
            <AlertDialogDescription>
              Ce tag sera retiré de tous les projets auxquels il est associé. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
